// Small reusable hooks: load a status-filtered queue, and manage bulk selection.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Invoice } from './types'
import { loadCostCenterLabels, loadExpenseTypeLabels, loadInvoicesByStatus } from './data'

export interface QueueState {
  invoices: Invoice[]
  loading: boolean
  error: string | null
  /** silent: refresh the rows without flipping into the loading state (used by
      the broker fence so the table doesn't flash a spinner on every re-check).
      Resolves with the rows this call fetched — the fence's give-up reads them
      directly rather than a possibly-stale state snapshot. */
  reload: (opts?: { silent?: boolean }) => Promise<Invoice[]>
}

export function useQueue(statuses: readonly string[], refreshKey: number = 0): QueueState {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Stable key so the effect doesn't re-run on every render from a new array ref.
  const key = statuses.join('|')
  // Monotonic request id: only the latest reload may write state, so two loads
  // resolving out of order can't leave the older payload showing.
  const seqRef = useRef(0)

  const reload = useCallback((opts?: { silent?: boolean }): Promise<Invoice[]> => {
    if (!opts?.silent) setLoading(true)
    setError(null)
    const seq = ++seqRef.current
    return loadInvoicesByStatus(statuses)
      .then((rows) => {
        if (seq === seqRef.current) setInvoices(rows)
        return rows
      })
      .catch((e: Error) => {
        if (seq === seqRef.current) setError(e.message)
        return [] as Invoice[]
      })
      .finally(() => {
        if (seq === seqRef.current) setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  // Reload on mount, when the status set changes, and whenever the parent bumps
  // refreshKey (the single top-bar Refresh).
  useEffect(() => {
    reload()
  }, [reload, refreshKey])

  return { invoices, loading, error, reload }
}

// PORT NOTE — the ~200-line "broker catch-up fence" that lived here is gone,
// along with the appointment poller. Both existed only because state advanced
// out of band: the client marked a row "submitting…", re-polled at 2, 4, 6, 8,
// 10, 15, 20 and 30 seconds, and gave up at 32. The decision endpoints are
// synchronous and return the new status, so a decision is either applied (and
// the response says to what) or refused with the reason. Do not reintroduce an
// optimistic status flip in its place.

export interface Selection {
  selected: Set<string>
  toggle: (id: string) => void
  toggleAll: () => void
  /** Bulk-set membership for a group of ids (per-company Select all — app.md §9 step 9). */
  setMany: (ids: string[], on: boolean) => void
  clear: () => void
  allSelected: boolean
  count: number
}

export function useSelection(ids: string[]): Selection {
  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Derive the valid selection (intersected with the current queue) rather than
  // pruning in an effect — ids that left the queue are simply ignored.
  const idSet = useMemo(() => new Set(ids), [ids])
  const valid = useMemo(() => new Set([...selected].filter((id) => idSet.has(id))), [selected, idSet])

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setSelected((prev) => (ids.every((id) => prev.has(id)) ? new Set() : new Set(ids)))
  }, [ids])

  const setMany = useCallback((toSet: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      toSet.forEach((id) => (on ? next.add(id) : next.delete(id)))
      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Set()), [])

  return {
    selected: valid,
    toggle,
    toggleAll,
    setMany,
    clear,
    allSelected: ids.length > 0 && ids.every((id) => selected.has(id)),
    count: valid.size,
  }
}

// ---------------------------------------------------------------------------
// Half labels
// ---------------------------------------------------------------------------

/** Code -> label for the two halves of an account, or the code itself while the
    masters are still loading and for a code the masters do not carry. */
export interface HalfLabels {
  expenseType: (code: string | null | undefined) => string | undefined
  costCenter: (code: string | null | undefined) => string | undefined
}

/**
 * The labels for "What is it?" and "Who is it for?".
 *
 * A line item carries codes and nothing else, so every surface that shows a half
 * has to resolve the label itself. Without this a line holding `67-502` rendered
 * an EMPTY picker — a line that is coded reading as one nobody has touched, at
 * the steps whose whole job is to tell those apart.
 *
 * The fallback is the code, never blank: a code is legible and an empty box is a
 * lie. `data.ts` memoizes both reads for the session, so mounting this on every
 * row of every table costs two requests in total.
 */
export function useHalfLabels(): HalfLabels {
  const [expenseTypes, setExpenseTypes] = useState<ReadonlyMap<string, string>>(EMPTY_LABELS)
  const [costCenters, setCostCenters] = useState<ReadonlyMap<string, string>>(EMPTY_LABELS)

  useEffect(() => {
    let live = true
    // Best-effort: a failed master read leaves the codes showing, which is the
    // same thing this hook falls back to for an unknown code.
    void loadExpenseTypeLabels().then((m) => { if (live) setExpenseTypes(m) }).catch(() => {})
    void loadCostCenterLabels().then((m) => { if (live) setCostCenters(m) }).catch(() => {})
    return () => { live = false }
  }, [])

  return useMemo(
    () => ({
      expenseType: (code) => (code ? (expenseTypes.get(code) ?? code) : undefined),
      costCenter: (code) => (code ? (costCenters.get(code) ?? code) : undefined),
    }),
    [expenseTypes, costCenters],
  )
}

const EMPTY_LABELS: ReadonlyMap<string, string> = new Map()
